import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import SessionProgressIndicator from '../../components/ui/SessionProgressIndicator';
import CollaborativeStatusBar from '../../components/ui/CollaborativeStatusBar';
import ShareSessionControl from '../../components/ui/ShareSessionControl';
import BillSummaryCard from './components/BillSummaryCard';
import ItemizedBillList from './components/ItemizedBillList';
import ParticipantManagementPanel from './components/ParticipantManagementPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { saveSessionToCloud, updateSessionParticipants, subscribeToSession, getSessionFromCloud } from '../../utils/supabaseService';
import { getCurrentSessionId, getSession } from '../../utils/sessionStorage';

const BillOverview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showSharePanel, setShowSharePanel] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [realtimeBillData, setRealtimeBillData] = useState(null);
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Get data from receipt scanner, shared session, or item-selection navigation
  const {
    sessionId: scannedSessionId,
    receiptData,
    billData: navigationBillData,
    isGuest,
    userName,
    userId
  } = location.state || {};

  // Transform scanned receipt data to bill format - NO usar mock data
  const getBillData = async () => {
    // Si viene billData desde navegación (item-selection -> bill-overview), usarlo directamente
    if (navigationBillData) {
      console.log('📥 Using billData from navigation state:', navigationBillData);
      setHasActiveSession(true);
      setIsLoading(false);
      return navigationBillData;
    }

    if (receiptData) {
      // Check if receiptData is already in full bill format (from shared session)
      if (receiptData.id && receiptData.restaurant && receiptData.items && receiptData.participants) {
        // Data is already in bill format
        // NO agregar guest aquí - ya fue agregado en JoinSession
        setHasActiveSession(true);
        setIsLoading(false);
        return receiptData;
      }

      // Transform receipt data from AI analysis to bill format
      setHasActiveSession(true);
      setIsLoading(false);
      return {
        id: scannedSessionId || 'bill-scanned-001',
        ticketNumber: scannedSessionId || 'SCANNED',
        restaurant: {
          name: receiptData.restaurant || 'Restaurante',
          address: '',
          phone: ''
        },
        tableNumber: null,
        date: receiptData.date || new Date().toISOString(),
        subtotal: receiptData.subtotal || 0,
        tax: receiptData.tax || 0,
        serviceCharge: 0,
        total: receiptData.total || 0,
        participants: [
          {
            id: userId || '1',
            name: userName || 'Tú',
            status: 'connected',
            isHost: true,
            joinedAt: new Date().toISOString()
          }
        ],
        items: (receiptData.items || []).map((item, index) => ({
          id: `item-${index + 1}`,
          name: item.name,
          description: '',
          category: 'general',
          quantity: item.quantity || 1,
          unitPrice: item.unitPrice || item.totalPrice,
          totalPrice: item.totalPrice,
          tags: []
        }))
      };
    }

    // Si no hay navigation data ni receiptData, intentar cargar sesión activa
    const currentSessionId = scannedSessionId || getCurrentSessionId();
    if (currentSessionId) {
      console.log('🔍 Intentando cargar sesión activa:', currentSessionId);

      // Intentar primero desde Supabase
      const cloudResult = await getSessionFromCloud(currentSessionId);
      if (cloudResult.success && cloudResult.data) {
        console.log('✅ Sesión cargada desde Supabase');
        setHasActiveSession(true);
        setIsLoading(false);
        return cloudResult.data;
      }

      // Si no está en Supabase, intentar desde localStorage
      const localSession = getSession(currentSessionId);
      if (localSession) {
        console.log('✅ Sesión cargada desde localStorage');
        setHasActiveSession(true);
        setIsLoading(false);
        return localSession;
      }
    }

    // NO hay sesión activa
    console.log('⚠️ No hay sesión activa disponible');
    setHasActiveSession(false);
    setIsLoading(false);
    return null;
  };

  useEffect(() => {
    getBillData().then(data => {
      if (data) {
        setRealtimeBillData(data);

        // 🔄 Inicializar selectedItems con los items que ya fueron validados
        const validatedItemIds = data.items
          ?.filter(item => item.validated === true)
          ?.map(item => item.id) || [];

        if (validatedItemIds.length > 0) {
          console.log('✅ Restaurando items validados:', validatedItemIds);
          setSelectedItems(validatedItemIds);
        }
      }
    });
  }, []);

  // Use realtime data if available
  const billData = realtimeBillData;
  const finalSessionId = scannedSessionId || getCurrentSessionId() || 'no-session';
  const connectedParticipants = billData?.participants?.filter(p => p?.status === 'connected');

  // Determine if current user is host (host is who scanned the ticket, not a guest joining)
  const isCurrentUserHost = !isGuest;

  // Only host can start splitting, and needs to have items selected/validated
  const canStartSplitting = isCurrentUserHost && selectedItems.length > 0;

  // Save session to cloud database when component mounts (only host saves initially)
  // NOTA: La sesión ya fue guardada en receipt-scanner, aquí la cargamos desde Supabase
  useEffect(() => {
    const loadSession = async () => {
      if (finalSessionId && isCurrentUserHost && !isGuest && finalSessionId !== 'no-session') {
        console.log('📥 Host: Loading session from Supabase:', finalSessionId);
        try {
          const result = await getSessionFromCloud(finalSessionId);
          if (result.success && result.data) {
            console.log('✅ Session loaded from Supabase:', result.data);
            // Usar los datos de Supabase que incluyen el participante con el nombre correcto
            setRealtimeBillData(result.data);

            // 🔄 Restaurar items validados
            const validatedItemIds = result.data.items
              ?.filter(item => item.validated === true)
              ?.map(item => item.id) || [];

            if (validatedItemIds.length > 0) {
              console.log('✅ Restaurando items validados desde Supabase:', validatedItemIds);
              setSelectedItems(validatedItemIds);
            }
          } else {
            console.log('⚠️ Session not found in Supabase, using local data');
          }
        } catch (error) {
          console.error('❌ Error loading session:', error);
        }
      }
    };

    loadSession();
  }, [finalSessionId, isCurrentUserHost, isGuest]); // Only run once on mount

  // Subscribe to real-time updates (only for host)
  useEffect(() => {
    if (isCurrentUserHost && finalSessionId) {
      console.log('🔔 Host: Subscribing to real-time updates for session:', finalSessionId);

      const unsubscribe = subscribeToSession(finalSessionId, (updatedData) => {
        console.log('🔔 Host: Received real-time update!', updatedData);
        console.log('🔔 New participants:', updatedData.participants);
        setRealtimeBillData(updatedData);
      });

      return () => {
        console.log('🔔 Host: Unsubscribing from session updates');
        unsubscribe();
      };
    } else {
      console.log('Not subscribing - isGuest:', isGuest, 'isCurrentUserHost:', isCurrentUserHost, 'sessionId:', finalSessionId);
    }
  }, [finalSessionId, isCurrentUserHost]);

  // Redirect guests directly to item selection (only once on mount, not on every update)
  useEffect(() => {
    if (isGuest && billData) {
      console.log('👤 Guest: Redirecting to item-selection');
      // Navigate guests directly to item selection with bill data
      navigate('/item-selection', {
        state: {
          sessionId: finalSessionId,
          billData: billData,
          userName: userName,
          userId: userId,
          isGuest: true
        },
        replace: true
      });
    }
  }, [isGuest, billData]); // Run when isGuest is set AND billData is available

  // Update Supabase when host selects/deselects items
  useEffect(() => {
    if (!isCurrentUserHost || !finalSessionId || !billData) {
      return;
    }

    console.log('🔄 Host selected items changed, updating Supabase...', selectedItems);

    // Mark selected items as validated
    const updatedBillData = {
      ...billData,
      items: billData.items.map(item => ({
        ...item,
        validated: selectedItems.includes(item.id)
      }))
    };

    // Save to Supabase (will trigger realtime update for guests)
    saveSessionToCloud(finalSessionId, updatedBillData)
      .then(() => {
        console.log('✅ Session updated in Supabase with validated items');
      })
      .catch(error => {
        console.error('❌ Error updating session:', error);
      });
  }, [selectedItems, isCurrentUserHost, finalSessionId]); // Trigger when selection changes

  const handleStartSplitting = () => {
    console.log('🚀 Comenzar División clicked');
    console.log('🚀 isCurrentUserHost:', isCurrentUserHost);
    console.log('🚀 selectedItems.length:', selectedItems.length);
    console.log('🚀 canStartSplitting:', canStartSplitting);
    console.log('🚀 billData:', billData);

    if (!isCurrentUserHost) {
      console.error('❌ No puedes comenzar la división porque eres invitado');
      return;
    }

    if (selectedItems.length === 0) {
      console.error('❌ Debes seleccionar al menos un artículo');
      alert('Por favor, selecciona al menos un artículo para validar');
      return;
    }

    console.log('✅ Navegando a item-selection');
    // Pass the validated bill data to item selection
    navigate('/item-selection', {
      state: {
        sessionId: finalSessionId,
        billData: billData,
        userName: userName || 'Tú',
        userId: userId,
        isHost: true
      }
    });
  };

  const handleInviteParticipant = () => {
    setShowSharePanel(true);
  };

  const handleViewParticipants = () => {
    // Toggle participant panel or show detailed view
    console.log('View all participants');
  };

  const handleRemoveParticipant = (participantId) => {
    console.log('Remove participant:', participantId);
    if (!billData) return;

    // Confirm action
    const participant = billData.participants?.find(p => p.id === participantId);
    if (!participant) return;

    const isSelf = String(participantId) === String(userId);
    const confirmMsg = isSelf ? '¿Estás seguro de que quieres salir de la sesión?' : `¿Quieres eliminar a ${participant.name} de la sesión?`;
    if (!confirm(confirmMsg)) return;

    // Remove participant locally
    const updatedParticipants = (billData.participants || []).filter(p => p.id !== participantId);
    const updatedBillData = { ...billData, participants: updatedParticipants };

    // Update local state
    setRealtimeBillData(updatedBillData);

    // Persist changes to Supabase (if session exists)
    if (finalSessionId) {
      updateSessionParticipants(finalSessionId, updatedParticipants)
        .then(res => {
          if (res.success) {
            console.log('✅ Participants updated in Supabase');
          } else {
            console.error('❌ Failed updating participants in Supabase:', res.error);
          }
        })
        .catch(err => console.error('❌ Error updating participants:', err));
    }

    // If the current user removed themselves, navigate away
    if (isSelf) {
      navigate('/');
    }
  };

  const handleItemSelect = (selectedItemIds) => {
    setSelectedItems(selectedItemIds);
  };

  const handleItemUpdate = async (itemId, updates) => {
    console.log('📝 Updating item:', itemId, updates);

    // Actualizar item en billData local
    const updatedBillData = {
      ...billData,
      items: billData.items.map(item =>
        item.id === itemId
          ? { ...item, ...updates }
          : item
      )
    };

    // Recalcular totales
    const newSubtotal = updatedBillData.items.reduce((sum, item) => sum + item.totalPrice, 0);
    const taxRate = billData.taxRate || 0.21;
    const newTax = newSubtotal * taxRate;
    const newTotal = newSubtotal + newTax;

    updatedBillData.subtotal = newSubtotal;
    updatedBillData.tax = newTax;
    updatedBillData.total = newTotal;

    // Actualizar estado local
    setRealtimeBillData(updatedBillData);

    // Guardar en Supabase
    if (finalSessionId) {
      const result = await saveSessionToCloud(finalSessionId, updatedBillData);
      if (result.success) {
        console.log('✅ Item updated in Supabase');
      } else {
        console.error('❌ Failed to update item in Supabase:', result.error);
      }
    }
  };

  const handleItemAdd = async (newItem) => {
    console.log('➕ Adding new item:', newItem);

    // Agregar nuevo item a billData
    const updatedBillData = {
      ...billData,
      items: [...billData.items, newItem]
    };

    // Recalcular totales
    const newSubtotal = updatedBillData.items.reduce((sum, item) => sum + item.totalPrice, 0);
    const taxRate = billData.taxRate || 0.21;
    const newTax = newSubtotal * taxRate;
    const newTotal = newSubtotal + newTax;

    updatedBillData.subtotal = newSubtotal;
    updatedBillData.tax = newTax;
    updatedBillData.total = newTotal;

    // Actualizar estado local
    setRealtimeBillData(updatedBillData);

    // Guardar en Supabase
    if (finalSessionId) {
      const result = await saveSessionToCloud(finalSessionId, updatedBillData);
      if (result.success) {
        console.log('✅ New item added to Supabase');
      } else {
        console.error('❌ Failed to add item to Supabase:', result.error);
      }
    }
  };

  const handleExportBill = () => {
    console.log('Export bill');
  };

  const handleViewFullBill = () => {
    console.log('View full bill details');
  };

  return (
    <div className="min-h-screen bg-background pb-24 lg:pb-6">
      <Header />
      <SessionProgressIndicator currentStep={1} />

      {/* Loading state */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <Icon name="Loader" size={48} className="text-primary animate-spin mb-4" />
          <p className="text-text-secondary">Cargando sesión...</p>
        </div>
      )}

      {/* No active session */}
      {!isLoading && !hasActiveSession && (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertCircle" size={32} className="text-text-secondary" />
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">
              No existen datos
            </h2>
            <p className="text-text-secondary mb-6">
              No hay una sesión activa. Por favor, escanea un ticket primero para crear una nueva sesión.
            </p>
            <Button
              variant="default"
              onClick={() => navigate('/receipt-scanner')}
              iconName="Camera"
              iconPosition="left"
            >
              Escanear Ticket
            </Button>
          </div>
        </div>
      )}

      {/* Active session content */}
      {!isLoading && hasActiveSession && billData && (
        <>
          <CollaborativeStatusBar
            participants={billData?.participants}
            sessionStatus="active"
            sessionId={finalSessionId}
            currentUserId={userId || billData.participants[0]?.id || '1'}
            onInviteParticipant={handleInviteParticipant}
            onViewParticipants={handleViewParticipants}
          />
          <div className="max-w-7xl mx-auto px-4 py-6 w-full overflow-x-hidden">
            {/* Page Header */}
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-semibold text-foreground mb-2">
                    Resumen de la Cuenta
                  </h1>
                  <p className="text-text-secondary">
                    Revisa los detalles de tu cuenta antes de comenzar la división entre comensales
                  </p>
                  {receiptData && (
                    <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-success/10 text-success text-sm rounded-full">
                      <Icon name="Sparkles" size={14} />
                      <span>Datos extraídos con IA del ticket escaneado</span>
                    </div>
                  )}
                </div>

                <div className="hidden lg:flex items-center space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/receipt-scanner')}
                    iconName="ArrowLeft"
                    iconPosition="left"
                    iconSize={16}
                  >
                    Volver
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setShowSharePanel(true)}
                    iconName="Share2"
                    iconSize={16}
                  >
                    Compartir
                  </Button>
                </div>
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Left Column - Bill Details */}
              <div className="lg:col-span-2 space-y-6 min-w-0 overflow-x-hidden">
                {/* Bill Summary */}
                <BillSummaryCard billData={billData} />

                {/* Itemized Bill */}
                <ItemizedBillList
                  items={billData?.items}
                  participants={billData?.participants}
                  onItemSelect={handleItemSelect}
                  onItemUpdate={handleItemUpdate}
                  onItemAdd={handleItemAdd}
                  initialSelectedItems={selectedItems}
                />
              </div>

              {/* Right Column - Participant Management & Actions */}
              <div className="space-y-6 min-w-0">
                {/* Participant Management */}
                <ParticipantManagementPanel
                  participants={billData?.participants}
                  sessionId={finalSessionId}
                  currentUserId={userId || billData.participants[0]?.id || '1'}
                  canStartSplitting={canStartSplitting}
                  onInviteParticipant={handleInviteParticipant}
                  onRemoveParticipant={handleRemoveParticipant}
                  onStartSplitting={handleStartSplitting}
                />

                {/* Note: actions are available inside the Comensales panel above. Removed duplicate Action Controls to avoid UI duplication. */}
              </div>
            </div>

            {/* Mobile Action Bar */}
            <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-surface border-t border-border p-4 z-30">
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={handleInviteParticipant}
                  iconName="UserPlus"
                  iconSize={16}
                  className="flex-1"
                >
                  Invitar
                </Button>
                <Button
                  variant="default"
                  disabled={!canStartSplitting}
                  onClick={handleStartSplitting}
                  iconName="ArrowRight"
                  iconPosition="right"
                  iconSize={16}
                  className="flex-2"
                >
                  Comenzar División
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Share Session Panel */}
      {showSharePanel && (
        <>
          <div
            className="fixed inset-0 bg-black/20 z-40"
            onClick={() => setShowSharePanel(false)}
          />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 max-w-md mx-auto">
            <div className="bg-surface border border-border rounded-lg shadow-elevation-3">
              <div className="flex items-center justify-between p-4 border-b border-border">
                <h3 className="font-semibold text-foreground">Compartir Sesión</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowSharePanel(false)}
                  className="h-8 w-8"
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>
              <div className="p-4">
                <ShareSessionControl
                  sessionId={finalSessionId}
                  position="inline"
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Mobile Bottom Padding */}
      <div className="lg:hidden h-20" />
    </div>
  );
};

export default BillOverview;